/**
 * Flaco's Family Cantina — storefront behaviour.
 * Ports the React client components (header, cart drawer, add-to-cart, shop
 * filters, contact form, scroll reveals) to vanilla JS on Shopify's Ajax API.
 */
(() => {
  "use strict";

  const FREE_SHIPPING_CENTS = 3000;
  const DEFAULT_MAX = 12;

  const money = (cents) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

  const products = (() => {
    try {
      return JSON.parse(document.querySelector("[data-flacos-products]")?.textContent || "[]");
    } catch {
      return [];
    }
  })();
  const productByHandle = Object.fromEntries(products.map((p) => [p.handle, p]));
  const maxFor = (handle) => productByHandle[handle]?.maxPerOrder || DEFAULT_MAX;

  /* ---------------------------------------------------------------- body lock */
  const locks = new Set();
  const lockBody = (key, locked) => {
    if (locked) locks.add(key);
    else locks.delete(key);
    document.body.style.overflow = locks.size ? "hidden" : "";
  };

  /* -------------------------------------------------------------- sticky header */
  const header = document.querySelector("[data-sticky-header]");
  if (header) {
    const wrapper = header.parentElement;
    if (wrapper && wrapper.classList.contains("shopify-section")) {
      wrapper.style.position = "sticky";
      wrapper.style.top = "0";
      wrapper.style.zIndex = "50";
    }
    const solid = ["border-cream/10", "bg-ink/80", "backdrop-blur-xl"];
    const clear = ["border-transparent", "bg-transparent"];
    const paint = () => {
      const scrolled = window.scrollY > 12;
      solid.forEach((c) => header.classList.toggle(c, scrolled));
      clear.forEach((c) => header.classList.toggle(c, !scrolled));
    };
    paint();
    window.addEventListener("scroll", paint, { passive: true });
  }

  /* --------------------------------------------------------------- mobile menu */
  const menuToggle = document.querySelector("[data-menu-toggle]");
  const mobileMenu = document.querySelector("[data-mobile-menu]");
  if (menuToggle && mobileMenu) {
    const links = [...mobileMenu.querySelectorAll("[data-mobile-link]")];
    const barTop = menuToggle.querySelector('[data-menu-bar="top"]');
    const barBottom = menuToggle.querySelector('[data-menu-bar="bottom"]');
    let open = false;
    const setMenu = (next) => {
      open = next;
      menuToggle.setAttribute("aria-expanded", String(open));
      mobileMenu.setAttribute("aria-hidden", String(!open));
      mobileMenu.classList.toggle("pointer-events-auto", open);
      mobileMenu.classList.toggle("opacity-100", open);
      mobileMenu.classList.toggle("pointer-events-none", !open);
      mobileMenu.classList.toggle("opacity-0", !open);
      barTop?.classList.toggle("top-1.5", open);
      barTop?.classList.toggle("rotate-45", open);
      barBottom?.classList.toggle("top-1.5", open);
      barBottom?.classList.toggle("-rotate-45", open);
      links.forEach((link, i) => {
        link.tabIndex = open ? 0 : -1;
        link.style.transitionDelay = open ? `${120 + i * 70}ms` : "0ms";
        link.style.transform = open ? "none" : "translateY(16px)";
        link.style.opacity = open ? "1" : "0";
      });
      lockBody("menu", open);
    };
    menuToggle.addEventListener("click", () => setMenu(!open));
    links.forEach((link) => link.addEventListener("click", () => setMenu(false)));
    matchMedia("(min-width: 768px)").addEventListener("change", (e) => {
      if (e.matches && open) setMenu(false);
    });
  }

  /* ------------------------------------------------------------- scroll reveal */
  const revealAll = () => {
    const nodes = document.querySelectorAll(".reveal:not(.is-visible)");
    if (typeof IntersectionObserver === "undefined") {
      nodes.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    nodes.forEach((el) => io.observe(el));
  };
  revealAll();

  /* --------------------------------------------------------------------- cart */
  const drawer = document.querySelector("[data-cart-drawer]");
  const cartState = { cart: null, open: false, listeners: new Set() };
  const onCartChange = (fn) => cartState.listeners.add(fn);
  const lineQty = (handle) => (cartState.cart?.items || []).filter((i) => i.handle === handle).reduce((sum, item) => sum + item.quantity, 0);

  const cartRequest = async (url, body) => {
    const response = await fetch(url, {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  const refreshCart = async () => {
    try {
      cartState.cart = await cartRequest("/cart.js");
    } catch {
      cartState.cart = cartState.cart || { items: [], item_count: 0, items_subtotal_price: 0 };
    }
    renderCart();
  };

  const addToCart = async (handle, qty = 1, variantId = null) => {
    const product = productByHandle[handle];
    if (!product || !product.variantId) return;
    const allowed = Math.max(0, Math.min(maxFor(handle) - lineQty(handle), qty));
    if (allowed > 0) {
      try {
        await cartRequest("/cart/add.js", { items: [{ id: variantId || product.variantId, quantity: allowed }] });
      } catch {
        /* Out of stock or network hiccup — the refreshed cart tells the truth. */
      }
    }
    await refreshCart();
    setDrawer(true);
  };

  const changeLine = async (key, quantity) => {
    try {
      await cartRequest("/cart/change.js", { id: key, quantity });
    } catch {
      /* ignore — refresh below */
    }
    await refreshCart();
  };

  const setDrawer = (open) => {
    if (!drawer) return;
    cartState.open = open;
    const backdrop = drawer.firstElementChild;
    const panel = drawer.querySelector("aside");
    drawer.setAttribute("aria-hidden", String(!open));
    drawer.classList.toggle("pointer-events-auto", open);
    drawer.classList.toggle("pointer-events-none", !open);
    backdrop.classList.toggle("opacity-100", open);
    backdrop.classList.toggle("opacity-0", !open);
    panel.classList.toggle("translate-x-0", open);
    panel.classList.toggle("translate-x-full", !open);
    lockBody("cart", open);
  };

  const lineTemplate = (item) => {
    const p = productByHandle[item.handle] || {};
    const name = p.name || item.product_title;
    const url = p.url || item.url;
    const image = item.image || p.image || "";
    const max = maxFor(item.handle);
    const atMax = item.quantity >= max;
    const meta = [p.category, item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : p.size].filter(Boolean).join(" · ");
    return `
      <li class="flex gap-4 py-5">
        <a href="${escapeHtml(url)}" data-cart-close class="relative h-24 w-20 shrink-0 overflow-hidden rounded-xl bg-smoke">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(name)}" class="img-fill object-cover">` : ""}
        </a>
        <div class="flex flex-1 flex-col">
          <div class="flex items-start justify-between gap-3">
            <div>
              <a href="${escapeHtml(url)}" data-cart-close class="display text-2xl leading-none text-cream hover:text-lava">${escapeHtml(name)}</a>
              ${meta ? `<p class="mt-1 text-xs text-cream/50">${escapeHtml(meta)}</p>` : ""}
            </div>
            <p class="text-sm font-semibold text-cream">${money(item.final_line_price)}</p>
          </div>
          <div class="mt-auto flex items-center justify-between pt-3">
            <div class="flex items-center rounded-full border border-cream/15">
              <button type="button" data-line-set="${escapeHtml(item.key)}" data-line-qty="${item.quantity - 1}" class="flex h-8 w-8 items-center justify-center text-cream/70 hover:text-cream" aria-label="Decrease ${escapeHtml(name)} quantity">−</button>
              <span class="w-6 text-center text-sm font-medium">${item.quantity}</span>
              <button type="button" data-line-set="${escapeHtml(item.key)}" data-line-qty="${item.quantity + 1}" ${atMax ? "disabled" : ""} class="flex h-8 w-8 items-center justify-center text-cream/70 hover:text-cream disabled:opacity-30" aria-label="Increase ${escapeHtml(name)} quantity">+</button>
            </div>
            <button type="button" data-line-set="${escapeHtml(item.key)}" data-line-qty="0" class="text-xs text-cream/45 underline-offset-4 hover:text-cream hover:underline">Remove</button>
          </div>
          ${p.maxPerOrder && atMax ? `<p class="mt-2 text-[0.65rem] uppercase tracking-widest text-lava">Limit ${p.maxPerOrder} per order</p>` : ""}
        </div>
      </li>`;
  };

  const suggestionTemplate = (p) => `
    <div class="flex items-center gap-3 py-2">
      <div class="relative h-12 w-10 shrink-0 overflow-hidden rounded-lg bg-smoke">
        ${p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.name)}" class="img-fill object-cover">` : ""}
      </div>
      <div class="flex-1">
        <p class="text-sm font-medium text-cream">${escapeHtml(p.name)}</p>
        <p class="text-xs text-cream/50">${money(p.price)}</p>
      </div>
      <button type="button" data-quick-add data-handle="${escapeHtml(p.handle)}" class="rounded-full border border-cream/20 px-3 py-1.5 text-xs font-medium text-cream transition-colors hover:border-lava hover:text-lava">Add</button>
    </div>`;

  const emptyTemplate = () => `
    <div class="flex h-full flex-col items-center justify-center text-center">
      <div class="mb-5 text-5xl">🫙</div>
      <p class="display text-3xl text-cream">Your cart is empty</p>
      <p class="mt-2 max-w-xs text-sm text-cream/55">Grab a bottle, twist the lid, and let the happy dance begin.</p>
      <a href="/collections/all" data-cart-close class="inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-wide transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-40 select-none bg-cream text-ink hover:bg-sand active:scale-[0.98] h-11 px-6 text-sm mt-8">Shop the sauces</a>
    </div>`;

  const renderCart = () => {
    const cart = cartState.cart || { items: [], item_count: 0, items_subtotal_price: 0 };
    const count = cart.item_count;
    const subtotal = cart.items_subtotal_price;
    const remaining = Math.max(0, FREE_SHIPPING_CENTS - subtotal);
    const progress = Math.min(100, (subtotal / FREE_SHIPPING_CENTS) * 100);

    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle("bg-ember", count > 0);
      el.classList.toggle("text-cream", count > 0);
      el.classList.toggle("bg-cream/10", count === 0);
      el.classList.toggle("text-cream/70", count === 0);
      const button = el.closest("button");
      if (button) button.setAttribute("aria-label", `Open cart, ${count} item${count === 1 ? "" : "s"}`);
    });

    if (!drawer) return;
    drawer.querySelector("[data-cart-drawer-count]").textContent = String(count);
    drawer.querySelector("[data-shipping-copy]").innerHTML =
      remaining > 0
        ? `You’re <span class="font-semibold text-lava">${money(remaining)}</span> away from free shipping`
        : `<span class="font-medium text-verde">🎉 You’ve unlocked free shipping</span>`;
    drawer.querySelector("[data-shipping-bar]").style.width = `${progress}%`;

    const body = drawer.querySelector("[data-cart-body]");
    if (cart.items.length === 0) {
      body.innerHTML = emptyTemplate();
    } else {
      const inCart = new Set(cart.items.map((i) => i.handle));
      const suggestions = products.filter((p) => p.available && p.variantId && !inCart.has(p.handle));
      body.innerHTML =
        `<ul class="divide-y divide-cream/10">${cart.items.map(lineTemplate).join("")}</ul>` +
        (suggestions.length
          ? `<div class="border-t border-cream/10 py-5"><p class="eyebrow mb-3 text-cream/50">Pairs well with</p>${suggestions.map(suggestionTemplate).join("")}</div>`
          : "");
    }

    const footer = drawer.querySelector("[data-cart-footer]");
    footer.classList.toggle("hidden", cart.items.length === 0);
    drawer.querySelector("[data-cart-subtotal]").textContent = money(subtotal);
    drawer.querySelector("[data-shipping-note]").textContent =
      remaining > 0 ? "Shipping calculated at checkout." : "Free shipping applied.";

    cartState.listeners.forEach((fn) => fn());
  };

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const quickAdd = target.closest("[data-quick-add]");
    if (quickAdd) {
      event.preventDefault();
      addToCart(quickAdd.dataset.handle, 1);
      return;
    }
    if (target.closest("[data-cart-open]")) {
      event.preventDefault();
      setDrawer(true);
      return;
    }
    const close = target.closest("[data-cart-close]");
    if (close) {
      if (close.tagName !== "A") event.preventDefault();
      setDrawer(false);
      return;
    }
    const lineButton = target.closest("[data-line-set]");
    if (lineButton) {
      event.preventDefault();
      changeLine(lineButton.dataset.lineSet, Number(lineButton.dataset.lineQty));
    }
  });

  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && cartState.open) setDrawer(false);
  });

  /* ------------------------------------------------------------- add to cart */
  document.querySelectorAll("[data-product-form]").forEach((form) => {
    const handle = form.dataset.handle;
    const max = Number(form.dataset.max) || maxFor(handle);
    const qtyEl = form.querySelector("[data-qty]");
    const dec = form.querySelector("[data-qty-dec]");
    const inc = form.querySelector("[data-qty-inc]");
    const add = form.querySelector("[data-add]");
    let qty = 1;
    let justAdded = false;

    const sync = () => {
      const inCart = lineQty(handle);
      const atLimit = inCart >= max;
      qty = Math.max(1, Math.min(qty, Math.max(1, max - inCart)));
      qtyEl.textContent = String(qty);
      inc.disabled = qty + inCart >= max;
      add.disabled = atLimit;
      add.textContent = atLimit ? `Limit ${max} per order` : justAdded ? "Added ✓" : "Add to cart";
    };

    dec.addEventListener("click", () => {
      qty = Math.max(1, qty - 1);
      sync();
    });
    inc.addEventListener("click", () => {
      qty = Math.min(max - lineQty(handle), qty + 1);
      sync();
    });
    add.addEventListener("click", async () => {
      add.disabled = true;
      await addToCart(handle, qty, document.querySelector(`[data-variant-select][data-handle="${handle}"]`)?.value);
      qty = 1;
      justAdded = true;
      sync();
      setTimeout(() => {
        justAdded = false;
        sync();
      }, 1400);
    });
    onCartChange(sync);
    sync();
  });

  const showProductPhoto = (handle, src, alt) => {
    const image = document.querySelector('[data-product-image="' + handle + '"]');
    if (!image || !src) return;
    image.removeAttribute("srcset");
    image.src = src;
    if (alt) image.alt = alt;
    document.querySelectorAll('[data-photo-handle="' + handle + '"]').forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.photoSrc === src));
    });
  };
  document.querySelectorAll("[data-photo-src]").forEach((button) => {
    button.addEventListener("click", () => showProductPhoto(button.dataset.photoHandle, button.dataset.photoSrc, button.dataset.photoAlt));
  });

  document.querySelectorAll("[data-variant-select]").forEach((select) => {
    select.addEventListener("change", () => {
      const variant = productByHandle[select.dataset.handle]?.variants?.find((item) => String(item.id) === select.value);
      if (!variant) return;
      const photo = productByHandle[select.dataset.handle]?.variantImages?.[String(variant.id)];
      showProductPhoto(select.dataset.handle, photo, productByHandle[select.dataset.handle].name + " — " + variant.title);
      document.querySelectorAll("[data-selected-price]").forEach((el) => { el.textContent = money(variant.price); });
      document.querySelectorAll("[data-selected-size]").forEach((el) => { el.textContent = variant.title + " bottle"; });
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    });
  });

  document.querySelectorAll("[data-notify-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const email = form.querySelector('input[type="email"]');
      if (!email || !email.value) return;
      const done = document.createElement("p");
      done.className = "text-sm text-verde";
      done.textContent = `You’re on the list — we’ll email you when ${form.dataset.name} drops.`;
      form.replaceWith(done);
    });
  });

  /* ------------------------------------------------------------ shop filters */
  const shop = document.querySelector("[data-shop]");
  if (shop) {
    const active = ["border-cream", "bg-cream", "text-ink"];
    const idle = ["border-cream/15", "text-cream/70", "hover:border-cream/40", "hover:text-cream"];
    const filters = { heat: "all", avail: "all" };
    const buttons = [...shop.querySelectorAll("[data-filter]")];
    const cards = [...shop.querySelectorAll("[data-shop-card]")];
    const count = shop.querySelector("[data-shop-count]");
    const empty = shop.querySelector("[data-shop-empty]");
    const grid = shop.querySelector("[data-shop-grid]");

    const heatOk = (heat) =>
      filters.heat === "all" ||
      (filters.heat === "mild" && heat <= 2) ||
      (filters.heat === "hot" && (heat === 3 || heat === 4)) ||
      (filters.heat === "very-hot" && heat === 5);

    const apply = () => {
      let shown = 0;
      cards.forEach((card) => {
        const ok = heatOk(Number(card.dataset.heat)) && (filters.avail === "all" || card.dataset.stock === "1");
        card.classList.toggle("hidden", !ok);
        if (ok) shown += 1;
      });
      count.textContent = `${shown} ${shown === 1 ? "sauce" : "sauces"}`;
      empty.classList.toggle("hidden", shown !== 0);
      grid.classList.toggle("hidden", shown === 0);
      buttons.forEach((button) => {
        const isActive = filters[button.dataset.filter] === button.dataset.value;
        button.setAttribute("aria-pressed", String(isActive));
        active.forEach((c) => button.classList.toggle(c, isActive));
        idle.forEach((c) => button.classList.toggle(c, !isActive));
      });
    };

    buttons.forEach((button) =>
      button.addEventListener("click", () => {
        filters[button.dataset.filter] = button.dataset.value;
        apply();
      }),
    );
    shop.querySelector("[data-shop-clear]")?.addEventListener("click", () => {
      filters.heat = "all";
      filters.avail = "all";
      apply();
    });
    apply();
  }

  /* ------------------------------------------------------------ contact form */
  const contact = document.querySelector("[data-contact-form]");
  if (contact) {
    const active = ["border-cream", "bg-cream", "text-ink"];
    const idle = ["border-cream/15", "text-cream/70", "hover:border-cream/40", "hover:text-cream"];
    const buttons = [...contact.querySelectorAll("[data-topic]")];
    const input = contact.querySelector("[data-topic-input]");
    const message = contact.querySelector("[data-topic-message]");
    const setTopic = (id) => {
      const chosen = buttons.find((b) => b.dataset.topic === id) || buttons[0];
      buttons.forEach((button) => {
        const isActive = button === chosen;
        button.setAttribute("aria-pressed", String(isActive));
        active.forEach((c) => button.classList.toggle(c, isActive));
        idle.forEach((c) => button.classList.toggle(c, !isActive));
      });
      if (input) input.value = chosen.textContent.trim();
      if (message) {
        message.placeholder =
          chosen.dataset.topic === "wholesale"
            ? "Tell us about your shop, restaurant or market — location, volume, and what you’re looking for."
            : "How can we help?";
      }
    };
    buttons.forEach((button) => button.addEventListener("click", () => setTopic(button.dataset.topic)));
    setTopic(new URLSearchParams(location.search).get("topic") || "order");
  }

  refreshCart();
})();
